import { useEffect, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Drawer } from './Drawer';

interface FilterDrawerProps<T> {
  open: boolean;
  /** 현재 적용된 값 — 열 때마다 초안으로 복사한다 */
  value: T;
  onClose: () => void;
  onApply: (value: T) => void;
  /** 초안과 초안 수정 함수로 섹션(FilterGroup)을 그린다 */
  children: (draft: T, patch: (next: Partial<T>) => void) => ReactNode;
}

/**
 * Figma 필터 드로어 — '필터' 제목 + 섹션들 + 하단 [취소] [적용].
 * 고른 값은 [적용]을 눌러야 반영된다.
 */
export function FilterDrawer<T extends object>({
  open,
  value,
  onClose,
  onApply,
  children,
}: FilterDrawerProps<T>) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (open) setDraft(value);
    // 열릴 때만 동기화 — value 객체는 렌더마다 새로 만들어진다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (next: Partial<T>) =>
    setDraft((prev) => ({ ...prev, ...next }));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      label="필터"
      title="필터"
      footer={
        <>
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            취소
          </Button>
          <Button className="flex-1" onClick={() => onApply(draft)}>
            적용
          </Button>
        </>
      }
    >
      {children(draft, patch)}
    </Drawer>
  );
}
